"use client";

import { type ReactNode, createContext, useContext } from "react";

type DesignSystemI18n = {
	required: string;
	autocomplete: {
		placeholder: string;
	};
	button: {
		showLess: string;
		showMore: (count: number) => string;
		clear: string;
	};
	timeInput: {
		openTimePicker: string;
		cancelButton: string;
		applyButton: string;
	};
	daysOfWeek: {
		monday: string;
		tuesday: string;
		wednesday: string;
		thursday: string;
		friday: string;
		saturday: string;
		sunday: string;
	};
	daysOfWeekShort: {
		monday: string;
		tuesday: string;
		wednesday: string;
		thursday: string;
		friday: string;
		saturday: string;
		sunday: string;
	};
	parkingOption: {
		startDateWithNoExpiry: string;
		startsNow: string;
		noExpiry: string;
		viewMore: (count: number) => string;
		everyDay: string;
		allDay: string;
		whenRegistered: string;
		userSelects: string;
		afterDuration: string;
		duration: (duration: { minutes: number; hours: number; days: number; weeks: number }) => string;
		afterDays: (count: number) => string;
		sameDay: string;
		specificDays: string;
		editDatesAndTimes: string;
	};
	show: (field: string) => string;
	hide: (field: string) => string;
	kanban?: {
		loadMore: string;
		noItems: string;
		dragHandleUsageInstructions: string;
		announce: {
			lifted: (params: { itemName: string; columnTitle: string }) => string;
			movedOver: (params: { itemName: string; columnTitle: string }) => string;
			dropped: (params: { itemName: string; columnTitle: string }) => string;
			cancelled: (params: { itemName: string; columnTitle: string }) => string;
		};
	};
};

const DesignSystemI18nContext = createContext<DesignSystemI18n | null>(null);

export const useDesignSystemI18n = () => {
	const context = useContext(DesignSystemI18nContext);
	if (!context) {
		throw new Error("useDesignSystemI18n must be used within a DesignSystemI18nProvider");
	}
	return context;
};

type DesignSystemI18nProviderProps = {
	children: ReactNode;
	value: DesignSystemI18n;
};

export const DesignSystemI18nProvider = ({ children, value }: DesignSystemI18nProviderProps) => (
	<DesignSystemI18nContext.Provider value={value}>{children}</DesignSystemI18nContext.Provider>
);
